import React, { useEffect, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import Button from '@carvana/core-helios-button';
import { Input, InputPropsWithMask } from '@carvana/core-helios-forms';
import { createNumberMask } from 'text-mask-addons';
import clsx from 'clsx';
import { AnalyticEvents } from '@constants/index';
import { Analytics } from '@services/analytics-service';
import { ROUTES, useRouter } from '@components/service-and-repair/providers/router';
import { useMileage } from '@components/service-and-repair/hooks/use-mileage';
import { useUserSelections } from '@components/service-and-repair/providers/user-selections';
import sarStyles from '../../service-and-repair.module.css';
import styles from './mileage.module.css';

const MAX_MILEAGE_LENGTH = 7;

const numberMask = createNumberMask({ prefix: '' });
// eslint-disable-next-line react/jsx-props-no-spreading
const MaskedInput = (props: InputPropsWithMask) => <Input {...props} />;

export const Mileage = () => {
  const { completeFormStep } = useRouter();
  const { updateUserSelections, userSelections, userFeedData } = useUserSelections();
  const { mileage } = userSelections;
  const { mileage: atSaleMileage, mileageLastUpdated, purchaseMileage } = userFeedData;
  const { saveMileage } = useMileage();
  const [mileageValue, setMileageValue] = useState(mileage ? mileage.toString() : '');
  const [touched, setTouched] = useState(!!mileageValue);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const disableNext = !!(!mileageValue || submitting || error);

  // SERV-405: Determine if we have VCM mileage data vs purchase mileage
  const hasVcmMileage = !!mileageLastUpdated;
  const displayMileage = atSaleMileage; // This is either VCM current or purchase mileage from CORE

  useEffect(() => {
    Analytics.trackEvent(AnalyticEvents.sarOdometerViewed);
    datadogRum.addAction(AnalyticEvents.sarOdometerViewed);

    // Set the height of the modal content to 100% - this will affect other parts of the modal so we only needed it here
    const modelContent = document.getElementById('modal-content');
    if (modelContent) {
      modelContent.style.height = '100%';
    }

    return () => {
      // Reset the height of the modal content to auto when the component unmounts
      if (modelContent) {
        modelContent.style.height = 'auto';
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value.replace(/,/g, ''), 10);
    setTouched(true);
    setError('');
    setMileageValue(e.target.value);

    if (!value || Number.isNaN(value)) {
      setError(' ');
      return false;
    }

    if (displayMileage && value > displayMileage) {
      return true;
    }

    setTouched(false);
  };

  const onMileageFocus = () => {
    Analytics.trackEvent(AnalyticEvents.sarOdometerFocused);
    datadogRum.addAction(AnalyticEvents.sarOdometerFocused);
  };

  const onHandleNext = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setTouched(true);
    const currentNumberValue = parseInt(mileageValue.replace(/,/g, ''), 10);

    // If we don't have mileage data, block submission entirely
    if (!displayMileage) {
      setError('Unable to validate mileage. Please refresh and try again.');
      setSubmitting(false);
      return false;
    }

    // Validate against the displayed mileage value
    if (currentNumberValue <= displayMileage) {
      setError(
        hasVcmMileage
          ? 'Current mileage must be higher than the last known mileage.'
          : 'Current mileage is lower than the mileage at purchase.'
      );
      setSubmitting(false);
      Analytics.trackEvent(AnalyticEvents.sarConfirmMileageTooLow, { mileageValue: currentNumberValue });
      datadogRum.addAction(AnalyticEvents.sarConfirmMileageTooLow, { mileageValue: currentNumberValue });
      return false;
    }

    saveMileage(currentNumberValue);
    updateUserSelections({ mileage: currentNumberValue });

    Analytics.trackEvent(AnalyticEvents.sarOdometerClickedSubmit, { mileageValue: currentNumberValue });
    datadogRum.addAction(AnalyticEvents.sarOdometerClickedSubmit, { mileageValue: currentNumberValue });

    completeFormStep(ROUTES.MILEAGE);

    setSubmitting(false);
  };

  return (
    <form className={sarStyles['page-wrapper']} onSubmit={onHandleNext} noValidate style={{ height: '100%' }}>
      <div className={sarStyles['page-header']}>
        <h2>Enter your vehicle&apos;s current mileage</h2>
        <div className={styles['page-header-subtitle']}>
          This is the <b>exact mileage from the odometer.</b> Estimating could lead to inaccurate coverage results.
        </div>
        <div className={styles['page-header-mileage']}>
          {hasVcmMileage ? 'Last known mileage:' : 'Mileage at purchase:'}{' '}
          <b>{displayMileage?.toLocaleString('en-US')}</b>
        </div>
      </div>

      <div className={clsx(sarStyles['form-wrapper'], styles['form-wrapper'])}>
        <MaskedInput
          label="Current Mileage"
          id="mileage-input"
          required
          inputMode="numeric"
          pattern="[0-9*]"
          maxLength={MAX_MILEAGE_LENGTH}
          mask={numberMask}
          onChange={handleInputChange}
          onBlur={handleInputChange}
          value={mileageValue}
          name="mileage"
          error={error}
          touched={touched}
          autoFocus
          setFieldValue={(n, v) => setMileageValue(v)}
          onFocus={onMileageFocus}
        />
      </div>

      <div className={sarStyles['button-wrapper']}>
        <Button type="submit" disabled={disableNext}>
          Next
        </Button>
      </div>
    </form>
  );
};

export default Mileage;
